/**
 * Determine the default route of the Container. This component is mapped to
 * `/` and will navigate to a route/component based on some criteria.
 *
 * - Locally, we show a small component that allows developers to pick
 *   contexts, shows some debugging information, and anything else we care to
 *   add in the future.
 *
 * - In higher environments (dev, test, prod), it will navigate to whichever
 *   route the `homepage-default-route` LD flag names, provided the user can
 *   actually get there.
 */

import { Navigate } from "react-router-dom";
import { type UserActions, type PermissionModel } from "@granular/authz";
import { useFeatureFlags, useOperatingContext } from "@granular/fabric3-core";
import {
  HOMEPAGE_ROUTE_BY_APP_KEY,
  HomepageAppKey,
  isHomepageAppKey,
} from "./constants";
import { checkAppPermissions } from "./helpers";
import { getSidebarVisibility } from "./helpers/sidebarVisibilityFlags";
import LocalHomepage from "./components/LocalHomepage";

export type PreferredHomepage = {
  /**
   * Whatever `homepage-default-route` served. Deliberately a plain `string`:
   * it's editable in the LaunchDarkly UI, so we treat it as untrusted until
   * `isHomepageAppKey` says otherwise.
   */
  key: string;
  /** Whether the app's own `app-<namespace>` flag says it's routable. */
  routable: boolean;
};

/**
 * Resolves the homepage route from the flag-chosen app, falling back through
 * what the user can actually access.
 *
 * Both inputs are trustworthy here. Permissions come from the tRPC context
 * query (loaded via Suspense, so always fresh by the time this runs), and the
 * LD client is now initialized *with* the identified user rather than
 * identifying them later in an effect — see `core`'s `FeatureFlags.tsx`. That
 * matters because this component navigates and unmounts: a value that arrives
 * after the first render would never be read at all.
 *
 * Priority order:
 * 1. The app named by `homepage-default-route`, if it's routable and permitted
 * 2. /trials-planning
 * 3. /profile — always accessible, safe fallback
 */
export const resolveHomepageRoute = (
  preferred: PreferredHomepage,
  userCan: (action: UserActions, model: PermissionModel) => boolean,
): string => {
  // Priority 1: the flag's choice. An unmapped or `default` flag value skips
  // straight past this, as does an app the user can't reach. The key doubles as
  // the permission model, so there's nothing to keep in sync.
  if (isHomepageAppKey(preferred.key)) {
    const app = HOMEPAGE_ROUTE_BY_APP_KEY[preferred.key];

    if (
      app &&
      preferred.routable &&
      checkAppPermissions(preferred.key, userCan)
    ) {
      return app.route;
    }
  }

  // Priority 2: trials planning — explicit secondary preference for agronomy
  // users who have trials access but not the flag-chosen app.
  //
  // NOTE: Unlike priority 1, this checks permission only, not routability —
  // carried over as-is from the previous implementation. A user with trials
  // permission but the app's flag off lands on the Coming Soon screen, which is
  // the correct product behaviour when the flag is off for that user.
  if (checkAppPermissions("app_trials_planning", userCan)) {
    return "/trials-planning";
  }

  // Priority 3: profile — always accessible regardless of context or permissions.
  return "/profile";
};

/**
 * Put everything together! Woooo! 🏠
 */
const Homepage: React.FC = () => {
  const isLocalhost = import.meta.url.includes("localhost");
  const { "homepage-default-route": homepageDefaultRouteFlag, ...restFlags } =
    useFeatureFlags<{ "homepage-default-route": HomepageAppKey }>();
  const { allContexts, userCan } = useOperatingContext();

  // Users with no linked operations go directly to profile.
  if (!allContexts?.length) {
    return <Navigate to="/profile" replace />;
  }

  const app = isHomepageAppKey(homepageDefaultRouteFlag)
    ? HOMEPAGE_ROUTE_BY_APP_KEY[homepageDefaultRouteFlag]
    : undefined;

  const homepageRoute = resolveHomepageRoute(
    {
      key: homepageDefaultRouteFlag,
      // Unmapped keys can't have a visibility flag to check, so don't invent
      // one — `resolveHomepageRoute` discards them anyway.
      routable: app
        ? getSidebarVisibility(restFlags, app.namespace).routable
        : false,
    },
    userCan,
  );

  return isLocalhost ? (
    <LocalHomepage routeIWouldNavigateTo={homepageRoute} />
  ) : (
    <Navigate to={homepageRoute} replace />
  );
};

export default Homepage;
